import {
  ResizeCoords,
  ResizeDirection,
  ResizeEndEventCallbackFunction,
  ResizeEventCallbackFunction,
  ResizeService,
  ResizeStartEventCallbackFunction,
  ResizeUnit,
  ResizeWidgetGetterFunction
} from '.'
import { Widget } from '..'
import { PermutatorService } from '../permutator'
import { WidgetHeightType } from '../widget/WidgetTypes'

class ResizeAction {
  protected containerElement: HTMLElement

  protected isTouch: boolean
  protected isResizing: boolean = false

  protected resizeUnitVert: ResizeUnit = ResizeUnit.grid
  protected resizeUnitHorz: ResizeUnit = ResizeUnit.grid

  protected maxDragDistanceX: number
  protected maxDragDistanceY: number
  protected initialWidth: number
  protected initialHeight: number
  protected colSize: number
  protected minWidth: number
  protected minHeight: number
  protected maxWidth: number = 32

  protected direction: ResizeDirection

  protected startPosition: ResizeCoords
  protected endPosition: ResizeCoords
  protected delta: ResizeCoords

  protected widgetGetter: ResizeWidgetGetterFunction

  protected onResizeHandlerFunc
  protected onResizeEndHandlerFunc

  protected $onResizeStart: ResizeStartEventCallbackFunction[] = []
  protected $onResize: ResizeEventCallbackFunction[] = []
  protected $onResizeEnd: ResizeEndEventCallbackFunction[] = []

  constructor(containerElement: HTMLElement, widgetGetterFunction: ResizeWidgetGetterFunction) {
    this.containerElement = containerElement
    this.widgetGetter = widgetGetterFunction
    this.onResizeHandlerFunc = this._onResize.bind(this)
    this.onResizeEndHandlerFunc = this._onResizeEnd.bind(this)
  }

  protected resetPosition(event) {
    this.startPosition = {
      x: this.isTouch ? event.touches[0].clientX : event.clientX,
      y: this.isTouch ? event.touches[0].clientY : event.clientY
    }
    this.endPosition = { x: 0, y: 0 }
    this.delta = { x: 0, y: 0 }
  }

  protected calculatePosition(event) {
    this.endPosition = {
      x: this.isTouch ? event.touches[0].clientX : event.clientX,
      y: this.isTouch ? event.touches[0].clientY : event.clientY
    }
    this.delta = {
      x: this.endPosition.x - this.startPosition.x,
      y: this.endPosition.y - this.startPosition.y
    }
  }

  protected clearSelection() {
    let selection = window.getSelection()

    if (selection) {
      selection.removeAllRanges()
    }
  }

  protected getNumDraggedCols(delta): number {
    let tolerance = this.colSize / 2
    let numDraggedCols = Math.sign(delta) * Math.floor(Math.abs(delta) / this.colSize)

    if ((Math.abs(delta) % this.colSize) >= tolerance) {
      numDraggedCols += Math.sign(delta)
    }

    return numDraggedCols
  }

  protected processHorizontalResize() {
    let numDraggedCols = this.getNumDraggedCols(this.delta.x)

    if (numDraggedCols > this.maxDragDistanceX) {
      numDraggedCols = this.maxDragDistanceX
    }

    let targetWidth = this.initialWidth + numDraggedCols

    if (targetWidth < this.minWidth) {
      targetWidth = this.minWidth
    }

    if (targetWidth > this.maxWidth) {
      targetWidth = this.maxWidth
    }

    // only emit event if size did actually change
    if (targetWidth === this.getWidget().size) {
      return
    }

    // notify listeners
    this.$onResize.forEach(listener => listener(this, targetWidth, ResizeDirection.horizontal))
  }

  protected processVerticalResize() {
    let numDraggedCols = 0

    if (this.resizeUnitVert === ResizeUnit.grid) {
      numDraggedCols = this.getNumDraggedCols(this.delta.y)
    }

    if (this.resizeUnitVert === ResizeUnit.pixel) {
      numDraggedCols = this.delta.y
    }

    // check if there is a height limit we need to apply
    if ((this.maxDragDistanceY > -1) && (numDraggedCols > this.maxDragDistanceY)) {
      numDraggedCols = this.maxDragDistanceY
    }

    let targetHeight = this.initialHeight + numDraggedCols

    if (targetHeight < this.minHeight) {
      targetHeight = this.minHeight
    }

    // only emit event if height did actually change
    if (targetHeight === this.getWidget().height) {
      return
    }

    // notify listeners
    this.$onResize.forEach(listener => listener(this, targetHeight, ResizeDirection.vertical))
  }

  protected _onResize(event) {
    this.calculatePosition(event)

    if (!this.isTouch) {
      event.preventDefault()
    }

    this.clearSelection()

    if (
      (this.direction === ResizeDirection.horizontal) ||
      (this.direction === ResizeDirection.any)
    ) {
      this.processHorizontalResize()
    }

    if (
      (this.direction === ResizeDirection.vertical) ||
      (this.direction === ResizeDirection.any)
    ) {
      this.processVerticalResize()
    }
  }

  protected _onResizeEnd() {
    this.isResizing = false

    document.removeEventListener(this.isTouch ? 'touchmove' : 'mousemove', this.onResizeHandlerFunc)
    document.removeEventListener(this.isTouch ? 'touchend' : 'mouseup', this.onResizeEndHandlerFunc)

    // notify listeners
    this.$onResizeEnd.forEach(listener => listener(this))
  }

  getWidget(): Widget {
    return this.widgetGetter()
  }

  onResizeStart(cb: ResizeStartEventCallbackFunction): this {
    if (this.$onResizeStart.includes(cb)) {
      return this
    }
    this.$onResizeStart.push(cb)
    return this
  }

  onResize(cb: ResizeEventCallbackFunction): this {
    if (this.$onResize.includes(cb)) {
      return this
    }
    this.$onResize.push(cb)
    return this
  }

  onResizeEnd(cb: ResizeEndEventCallbackFunction): this {
    if (this.$onResizeEnd.includes(cb)) {
      return this
    }
    this.$onResizeEnd.push(cb)
    return this
  }

  beginResize(event, direction: ResizeDirection, resizeUnitVert: ResizeUnit = ResizeUnit.grid, resizeUnitHorz: ResizeUnit = ResizeUnit.grid) {
    this.isTouch = event.type === 'touchstart'
    if (!this.isTouch && event.button !== 0) {
      return
    }

    this.resizeUnitVert = resizeUnitVert
    this.resizeUnitHorz = resizeUnitHorz

    this.direction = direction

    this.resetPosition(event)

    this.isResizing = true

    document.addEventListener(this.isTouch ? 'touchmove' : 'mousemove', this.onResizeHandlerFunc)
    document.addEventListener(this.isTouch ? 'touchend' : 'mouseup', this.onResizeEndHandlerFunc)

    let widget = this.getWidget()
    let row = PermutatorService.getParentRow(widget.wbUUID!)

    this.colSize = this.containerElement.offsetWidth / widget.size!
    this.maxDragDistanceX = row ? PermutatorService.getRemainingColCount(row) : 0
    this.initialWidth = widget.size!
    this.maxDragDistanceY = -1

    if ((direction === ResizeDirection.any) || (direction === ResizeDirection.vertical)) {
      this.maxDragDistanceY = ResizeService.getMaxVerticalDragDistance(widget, resizeUnitVert)
    }

    let minWidth = widget.minSize! > 1 ? widget.minSize! : 1

    // if this widget has nested contents the contents define the minimum dimensions for resizing
    if (widget.rows) {
      let forcedContentWidth = minWidth

      widget.rows.forEach(row => {
        let rowWidth = 0
        row.widgets.forEach(widget => rowWidth += widget.size!)

        if (rowWidth > forcedContentWidth) {
          forcedContentWidth = rowWidth
        }
      })

      if (forcedContentWidth > minWidth) {
        minWidth = forcedContentWidth
      }
    }

    this.minWidth = minWidth

    this.initialHeight = parseInt(String(widget.height!), 10) || 1
    this.minHeight = ResizeService.getMinHeightForWidget(widget, (resizeUnitVert === ResizeUnit.grid) ? WidgetHeightType.grid : WidgetHeightType.pixel)

    // notify listeners
    this.$onResizeStart.forEach(listener => listener(this, direction))
  }
}

export { ResizeAction }
